import React, {useState, useEffect, Fragment, lazy, Suspense} from 'react';
import "./ToggleSidebar.scss";
import SvgIcon from '../SvgIcon';
import Button from '../Button';
import classNames from 'classnames';
import Stat from '../Stat';
import {mapUserGatewayProfileType, userProfileType, siteLocale, gaEventLabel, gaEventCategories } from '../../lib/constants';
const EngagementWord = lazy(() => import('../EngagementWord/EngagementWord'));
const EngagementImage = lazy(() => import('../EngagementImage/EngagementImage'));
const EngagementInfo = lazy(() => import('../EngagementInfo/EngagementInfo'));
const SideMenu = lazy(() => import('./SideMenu'));

const ToggleSidebar = ({isLoggedIn, user, children, openAuthModal, t, isRtl, viewport, location, engagementData, readCount, fetchEngagementWorkFlow, isStudentProfile, filters, fetchAllListsFiltersWorkflow, fetchAllReadalongFiltersWorkflow, raFilters}) => {
    const baseClassName = "pb-toggle-sidebar";

    const [open,setOpen] = useState(false);

    useEffect(() => {
      if(viewport.medium) setOpen(true)
      else setOpen(false)
    }, [viewport])

    useEffect(() => {
      if(isStudentProfile) fetchEngagementWorkFlow()
    },[isStudentProfile, isLoggedIn])


    const wrapperClasses = {
      [`${baseClassName}__container`]: true,
      [`${baseClassName}__container--rtl`]: isRtl,
    }

    const containerClasses = {
      [`${baseClassName}__main-menu`]: true,
      [`${baseClassName}__main-menu--open`]: open && viewport.medium,
      [`${baseClassName}__main-menu--mobile-open`]: open && !viewport.medium,
      [`${baseClassName}__main-menu--full-width`]: open && viewport.medium && window.location.pathname.replace(`/${siteLocale}`, '').replace(/\/([^/]*).*$/, '$1') === "reading-programme",
    }

    const subMenuClasses = {
      [`${baseClassName}__sub-menu-section`]: true,
      [`${baseClassName}__sub-menu-section--rtl`]: isRtl,
      [`${baseClassName}__sub-menu-section--collapse`]: !open && viewport.medium,
      [`${baseClassName}__sub-menu-section--mobile-collapse`]: !open && !viewport.medium,
    }

    const circleClasses = {
      [`${baseClassName}__circle`]:(open || viewport.medium),
      [`${baseClassName}__circle--rtl`]:isRtl,
    }

    const welcomeClasses = {
      [`${baseClassName}__welcome-section`]:true,
      [`${baseClassName}__welcome-section--rtl`]:isRtl,
      [`${baseClassName}__welcome-section__login`]:isLoggedIn,
    }

    const chevronRight = viewport.medium ? (open ? "chevron-right" : "menu"): (open && "chevron-right");
    const chevronLeft = viewport.medium ? (open ? "chevron-left" : "menu"): (open && "chevron-left");



    const Welcome = () => {
      return(
        <div className={classNames(welcomeClasses)}>
          {!viewport.medium &&
            <div className={isLoggedIn? `${baseClassName}__circle_log--mobile`:`${baseClassName}__circle--mobile`} onClick={() =>{setOpen(!open);onTriggerEvents({event: gaEventLabel.clickedSideMenu, label: gaEventLabel.mobileApp})}}>
              <SvgIcon name={"menu"} />
            </div>
          }

        {isLoggedIn ? (
          <div className={`${baseClassName}__welcome-section--space`}>
              <div className={`${baseClassName}__welcome-title`}>
              {user.profile_type === mapUserGatewayProfileType[userProfileType.STUDENT] ?
                <h3 className={`${baseClassName}__name`}>{t("global.hi")}, {user.first_name}</h3> :
                <h3 className={`${baseClassName}__name`}>{t("global.hi")}!</h3> 
              }
              <p className={`${baseClassName}__welcome-sub-title`}>{ user.profile_type === mapUserGatewayProfileType[userProfileType.STUDENT] && user.signInCount > 1 && t("global.see-you-again")}</p>
              </div>
              { (readCount > 0) && isStudentProfile &&
              <div className={isRtl ? `${baseClassName}__welcome-img ${baseClassName}__welcome-img--rtl`:`${baseClassName}__welcome-img`} >
                {user.profile_type === mapUserGatewayProfileType[userProfileType.STUDENT] && <Fragment>
                  { <Stat parentClassName={isRtl ? `${baseClassName}__welcome-count ${baseClassName}__welcome-count--rtl`:`${baseClassName}__welcome-count`} value={readCount} shortFormatMinValue={1000} shortFormatPrecision ={1} /> }
                  { <p className={isRtl ? `${baseClassName}__welcome-read ${baseClassName}__welcome-read--rtl`: `${baseClassName}__welcome-read`}>{t("global.read-home")}</p> }
                  </Fragment>}
               </div>}
          </div>
        ) : (
          <div className={`${baseClassName}__welcome-title`}>
            <h3>{t("global.guest")}</h3>
            <p className={`${baseClassName}__welcome-sub-title`}>{t("global.log-in-for-better-exp")}</p>
            <Button
              parentClassName={isRtl? `${baseClassName}__signup ${baseClassName}__signup--rtl`:`${baseClassName}__signup`}
              label={t("global.sign-up-log-in")}
              variant="primary"
              fullWidth
              isRtl={isRtl}
              onClick={openAuthModal}
              />
          </div>
        )}
      </div>
      );
    }

    const EngagementContent = () => {
      if(engagementData.type === "pic_of_the_day") {
        return <Suspense fallback={null}><EngagementImage t={t} image={engagementData.image} desc={engagementData.content.description} url={`/${siteLocale}`+ engagementData.redirection_url} isRtl={isRtl} user={user} /></Suspense>
      }
      else if (engagementData.type === "did_you_know") {
        return <Suspense fallback={null}><EngagementWord t={t} title={engagementData.translated_type} desc={ engagementData.content.data} url={`/${siteLocale}`+ engagementData.redirection_url} isRtl={isRtl} /></Suspense>
      }
      else {
        return <Suspense fallback={null}><EngagementInfo t={t} title={engagementData.translated_type} data={engagementData.content.data} desc={engagementData.content.description} url={`/${siteLocale}`+ engagementData.redirection_url} isRtl={isRtl} user={user}/></Suspense>
      }
    }

    const onTriggerEvents = (obj) => {
      const {gtmCat, event, label, itemType, itemValue, itemId} = obj;
      window.dataLayer.push({
        'category': gtmCat || gaEventCategories.homePageStudent,
        'event': event,
        'label': label,
        'item_type': itemType || null,
        'item_value': itemValue || null, 
        'item_id': itemId || null,
        'user_profile': user.profile_type,
        'user_id': user.id,
      })
    }
  
  return (
    <div className={isStudentProfile ? classNames(wrapperClasses) : ''}>       
      {isStudentProfile && <div className={classNames(subMenuClasses)}>     
        <div className={classNames(circleClasses)} onClick={() =>setOpen(!open) }>
          <SvgIcon name={isRtl? chevronRight: chevronLeft } title={open ? t("global.close-menu") :t("global.open-menu")}/>
        </div>
        
        { 
        <div className={open ? `${baseClassName}__content` : `${baseClassName}__content--hidden`}>
          {viewport.medium && <Welcome />}
          <Suspense fallback={null}><SideMenu parentClassName={baseClassName} isRtl={isRtl} user={user} location={location} t={t} open={open} isLoggedIn={isLoggedIn} fetchEngagementWorkFlow={fetchEngagementWorkFlow} viewport={viewport} filters={filters} fetchAllListsFiltersWorkflow={fetchAllListsFiltersWorkflow} fetchAllReadalongFiltersWorkflow={fetchAllReadalongFiltersWorkflow} raFilters={raFilters}/></Suspense>
          <div className={`${baseClassName}__engagement`} dir={isRtl?"rtl":"ltr"}>
            {engagementData && engagementData.content &&
              <EngagementContent />
            }
          </div>
        </div>}
      </div>}
      <div className={isStudentProfile ? classNames(containerClasses) : null}>
        {!viewport.medium && isStudentProfile &&  <Welcome />}
        {children}
      </div>
    </div>
  );
}

export default ToggleSidebar



// WEBPACK FOOTER //
// ./src/components/ToggleSidebar/ToggleSidebar.jsx